/**
 * @packageDocumentation
 * @module      EmcDisplayDataManager.ts
 * @description Encapsulates the logic to fetch the data from the polled packets to be given to the data element that supplies to the UI.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
import EmcSystemCollection from "./EmcSystemCollection";
import EmcSystem from "./EmcSystem";
import CloudApi from "../CloudApi";
import Fetcher from "../Fetcher";
import EmcConsts from "./EmcConsts";
import ConsoleLogger from "../ConsoleLogger";
import EmcRcFaults from "./EmcRcFaults";
import { EmcFaultSeverity } from "./EmcEnums";
export class EmcDisplayDataManager {
    constructor() {
        // All systems
        this.allSystemsList = [];
        this.emcSignals = {
            ScreenDefocussed: false,
            IdleMonitor: false,
            TestDisable: true,
            UserInput: false,
            FullSystem: false,
            ArmLockUnlock: false,
            DriveJog: false,
            EmcReset: false,
            EmcMode: false,
            RotJog1: true,
            RotJog2: true,
            RotJog3: true,
            RotJog4: true
        };
        this.patientSubsystemsVersionData = null;
        this.controlSubsystemsVersionData = null;
        this.controlConnectionStatus = "";
        this.communicationFaultCount = 0;
        this.psrc = null;
        this.csrc = null;
    }
    fetchAllSystemData() {
        return new CloudApi(new Fetcher()).getEmcSystems().then((result) => {
            this.allSystemsList = result;
            this.allSystemsList.sort((a, b) => a.systemName.localeCompare(b.systemName));
            return Promise.resolve();
        }, (error) => {
            return Promise.reject('The server could not be reached.');
        });
    }
    adjustLagInPacketifPresent(systemId) {
        let emcSystem = EmcSystemCollection.getSystem(systemId);
        let emcPacket;
        let isLagPresent = emcSystem === null || emcSystem === void 0 ? void 0 : emcSystem.isLagPresent();
        if (false == isLagPresent) {
            emcPacket = emcSystem === null || emcSystem === void 0 ? void 0 : emcSystem.getCurrentPacket();
        }
        else {
            let packetCount = 0;
            let currentFaultMessages = [];
            let removedFaultMessages = [];
            let stateChangeMessages = [];
            while (packetCount < EmcConsts.MAX_LAG_PACKETS_COUNT) {
                emcPacket = emcSystem === null || emcSystem === void 0 ? void 0 : emcSystem.getCurrentPacket();
                if (emcPacket) {
                    currentFaultMessages.push(...emcPacket.currentFaultMessages);
                    removedFaultMessages.push(...emcPacket.removedFaultMessages);
                    stateChangeMessages.push(...emcPacket.stateChangeMessages);
                }
                packetCount++;
            }
            if (emcPacket) {
                emcPacket.currentFaultMessages = currentFaultMessages;
                emcPacket.removedFaultMessages = removedFaultMessages;
                emcPacket.stateChangeMessages = stateChangeMessages;
            }
        }
        return emcPacket;
    }
    fetchActiveSystemData(systemId) {
        ConsoleLogger.log("Inside Data Manager's fetchActiveSystemData");
        let emcSystem = EmcSystemCollection.getSystem(systemId);
        let emcPacket = this.adjustLagInPacketifPresent(systemId);
        ConsoleLogger.log("The value of emcSystem is ", emcSystem);
        ConsoleLogger.log("The value of emcPacket is ", emcPacket);
        if (undefined != emcPacket) {
            this.updateSideSpecificSystemData(emcPacket);
            this.updateRcFaults(emcPacket.currentFaultMessages, emcPacket.removedFaultMessages);
            return emcPacket;
        }
    }
    updateRcFaults(currentFaultMessages, removedFaultMessages) {
        currentFaultMessages.forEach(faultMessage => {
            var _a, _b, _c, _d;
            let faultIdField = "";
            switch (faultMessage.subsystem) {
                case "PSRC":
                    {
                        for (const id of this.psrc.fieldMap.keys()) {
                            if (faultMessage.id.startsWith(id.toString())) {
                                faultIdField = id;
                                break;
                            }
                        }
                        if ("" != faultIdField) {
                            if (faultMessage.severity === EmcFaultSeverity.CLEARED)
                                (_a = this.psrc) === null || _a === void 0 ? void 0 : _a.fieldMap.set(faultIdField, "0");
                            else
                                (_b = this.psrc) === null || _b === void 0 ? void 0 : _b.fieldMap.set(faultIdField, faultMessage.data[0]);
                        }
                        break;
                    }
                case "CSRC":
                    {
                        for (const id of this.csrc.fieldMap.keys()) {
                            if (faultMessage.id.startsWith(id.toString())) {
                                faultIdField = id;
                                break;
                            }
                        }
                        if ("" != faultIdField) {
                            if (faultMessage.severity === EmcFaultSeverity.CLEARED)
                                (_c = this.csrc) === null || _c === void 0 ? void 0 : _c.fieldMap.set(faultIdField, "0");
                            else
                                (_d = this.csrc) === null || _d === void 0 ? void 0 : _d.fieldMap.set(faultIdField, faultMessage.data[0]);
                        }
                        break;
                    }
                default:
                    break;
            }
        });
    }
    updateSignals(emcSignals) {
        this.emcSignals = emcSignals;
    }
    updateActiveSystem(systemId) {
        let emcSystem = EmcSystemCollection.getSystem(systemId);
        if (true == this.emcSignals.EmcMode) {
            if (undefined == emcSystem) {
                emcSystem = new EmcSystem(systemId);
                EmcSystemCollection.addSystem(emcSystem);
                this.psrc = new EmcRcFaults();
                this.csrc = new EmcRcFaults();
            }
        }
        else {
            this.patientSubsystemsVersionData = null;
            this.controlSubsystemsVersionData = null;
            this.arm = null;
            this.drive = null;
            this.frame = null;
            this.csPdo = null;
            this.psPdo = null;
            this.latency = EmcConsts.NOT_AVAILABLE;
            this.controlConnectionStatus = EmcConsts.NOT_AVAILABLE;
            this.communicationFaultCount = 0;
            this.pim = null;
            this.psrc = null;
            this.csrc = null;
        }
        if (undefined != emcSystem)
            emcSystem.emcSignals = JSON.parse(JSON.stringify(this.emcSignals));
    }
    removeInactiveSystem(systemId) {
        EmcSystemCollection.removeSystem(systemId);
    }
    updateSideSpecificSystemData(emcPacket) {
        var _a, _b, _c, _d, _e, _f;
        // CS
        if (true == emcPacket.isControlSide) {
            ConsoleLogger.log("Inside the emcDisplayDataManager isControlSide");
            this.pim = (_a = emcPacket.emcDataParameterAllTabs) === null || _a === void 0 ? void 0 : _a.emcPIMPDO;
            this.csPdo = (_b = emcPacket.emcDataParameterAllTabs) === null || _b === void 0 ? void 0 : _b.pdoData;
            this.controlSubsystemsVersionData = emcPacket.emcSubsystemsVersionData;
        }
        // PS
        else {
            ConsoleLogger.log("Inside the emcDisplayDataManager isPatientSide");
            this.arm = (_c = emcPacket.emcDataParameterAllTabs) === null || _c === void 0 ? void 0 : _c.emcDataParameter_Arm;
            this.drive = (_d = emcPacket.emcDataParameterAllTabs) === null || _d === void 0 ? void 0 : _d.emcDataParameter_Drive;
            this.frame = (_e = emcPacket.emcDataParameterAllTabs) === null || _e === void 0 ? void 0 : _e.emcDataParameter_Frame;
            this.psPdo = (_f = emcPacket.emcDataParameterAllTabs) === null || _f === void 0 ? void 0 : _f.pdoData;
            this.latency = emcPacket.latency;
            this.controlConnectionStatus = emcPacket.controlConnectionStatus;
            this.communicationFaultCount = emcPacket.communicationFaultCount;
            this.patientSubsystemsVersionData = emcPacket.emcSubsystemsVersionData;
        }
    }
}
//# sourceMappingURL=EmcDisplayDataManager.js.map